<template>
  <ModalDefault :show="show" size="extraLarge" hide-icon @close="close">
    <div v-if="gift" class="md:flex -mx-8 md:-mx-10 -mt-16 md:-mb-8">
      <div
        class="w-full md:w-1/2 max-w-full xl:max-w-lg p-3 lg:p-8 shrink-0 bg-gray-50 md:max-h-[80vh] md:overflow-y-auto border-b md:border-b-0 md:border-r border-gray-200"
      >
        <div class="relative w-full overflow-hidden">
          <img :src="activeImage" :alt="gift.name" class="w-full aspect-square rounded-[12px]" />
          <ul class="absolute h-10 left-3 bottom-3 w-full flex gap-x-3">
            <li v-for="image in images" :key="image" class="shrink-0">
              <button type="button" @click="activeImage = image">
                <img
                  :src="image"
                  :alt="gift.name"
                  class="w-10 h-10 rounded border"
                  :class="image === activeImage ? 'border-blue-300' : 'border-black/5'"
                />
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div
        class="flex-grow flex flex-col justify-between p-3 lg:p-8 md:max-h-[80vh] md:overflow-y-auto"
      >
        <div>
          <p v-if="isKadonationVoucher" class="mb-3 md:mb-6">
            <span class="heading-3">{{ $t("gifts_v2.amount_of_choice") }}</span>
            <span class="text-xs font-medium text-gray-400">
              &nbsp;{{ $t("gifts_v2.excluding_shipping") }}</span
            >
          </p>
          <p v-else class="mb-3 md:mb-6">
            <span v-if="priceHasEuro" class="text-sm text-black">&euro;</span>
            <span class="heading-1">{{ priceFormatted }}</span>
            <span class="text-xs font-medium text-gray-400">
              &nbsp;{{ $t("gifts_v2.excluding_vat_and_shipping") }}</span
            >
          </p>

          <h2 class="heading-2 mb-3">{{ gift.name }}</h2>
          <div v-if="giftDescription" class="mt-1 mb-3 text-gray-500">
            <div
              v-if="showFullDescription"
              class="space-y-4 mb-1"
              data-testid="gift-description"
              v-html="giftDescription"
            ></div>
            <p v-else class="inline" data-testid="gift-excerpt">
              {{ excerpt }}
            </p>
            <ActionLink
              v-if="hasLongContent"
              type="button"
              class="inline-block"
              data-testid="read-more-less-button"
              @click="showFullDescription = !showFullDescription"
            >
              {{ showFullDescription ? $t("general.read_less") : $t("general.read_more") }}
            </ActionLink>
          </div>
          <div v-if="hasOptions" class="mb-6">
            <SelectSingle
              id="gift-option"
              v-model="selectedOption"
              :options="options"
              :label="$t('gifts_v2.option')"
              @update:model-value="onOptionUpdate"
            >
              <template #button>
                <div v-if="selectedOption" class="w-full flex items-center justify-between">
                  <span class="block truncate font-medium">
                    {{ selectedOption?.name }}
                    <span
                      v-if="isKadonationVoucher && selectedOption.extra_cost"
                      class="text-gray-400"
                    >
                      + {{ selectedOption.extra_cost }}
                    </span>
                    <span v-else-if="selectedOption.price" class="text-gray-400">
                      {{ selectedOption.price }}
                    </span>
                  </span>

                  <ChevronUpDownIcon
                    class="shrink-0 text-gray-500 transform duration-300 w-5 h-5"
                    aria-hidden="true"
                  />
                </div>
              </template>
              <template #option="{ option }">
                <span
                  class="w-full flex items-center justify-between gap-3 font-medium text-gray-400 hover:text-gray-500"
                >
                  <span class="flex items-center gap-3">
                    <CImg
                      height="80"
                      width="80"
                      :src="option.images?.[0] || defaultGiftImage"
                      class="w-12 h-12 rounded-sm"
                    />
                    <span>
                      {{ option.name }}
                    </span>
                  </span>
                  <span class="text-xs italic pr-3">
                    <template v-if="isKadonationVoucher && option.extra_cost">
                      + {{ option.extra_cost }}
                    </template>
                    <template v-else-if="option.price">
                      {{ option.price }}
                    </template>
                  </span>
                </span>
              </template>
            </SelectSingle>
          </div>
        </div>
        <div>
          <Disclosure
            type="info"
            :title="$t('gifts_v2.grid-preview.gift-detail-modal.disclosure.title')"
            :description="$t('gifts_v2.grid-preview.gift-detail-modal.disclosure.description')"
            class="mb-6"
          />
          <div class="flex items-center gap-3">
            <TertiaryButton data-testid="cancel-button" @click="cancel">{{
              $t("general.cancel")
            }}</TertiaryButton>
            <PrimaryButton data-testid="use-this-gift-button" @click="emit('primary-click')">{{
              $t("gifts_v2.grid-preview.gift-detail-modal.create_account_button")
            }}</PrimaryButton>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="min-w-[80vw] h-[80vh] md:h-auto flex items-center justify-center md:mb-8">
      <LoadingIcon data-testid="loading-icon" class="my-40 w-8 h-8 text-gray-400" />
    </div>
  </ModalDefault>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { useWindowSize } from "@vueuse/core"
import ModalDefault from "@/components/elements/modals/ModalDefault.vue"
import ActionLink from "@/components/elements/links/ActionLink.vue"
import SelectSingle from "@/components/elements/inputs/SelectSingle.vue"
import CImg from "@/components/elements/CImg.vue"
import httpClient from "@/helpers/http"
import { ChevronUpDownIcon, LoadingIcon } from "@/components/icons"
import defaultGiftImage from "@/assets/default-gift-image.jpeg"
import PrimaryButton from "@/components/elements/buttons/PrimaryButton.vue"
import TertiaryButton from "@/components/elements/buttons/TertiaryButton.vue"
import { errorToast } from "@/helpers/toasts"
import Disclosure from "@/components/elements/disclosures/Disclosure.vue"

const props = defineProps({
  giftId: [String, Number],
  show: Boolean,
})

const emit = defineEmits(["close", "cancel", "primary-click"])

const gift = ref(undefined)
const options = ref(undefined)
const selectedOption = ref(undefined)

const onOptionUpdate = () => {
  activeImage.value = selectedOption.value?.images?.[0] || gift.value?.images?.[0]
}

const price = computed(() =>
  selectedOption.value ? selectedOption.value.price : gift.value?.price,
)
const priceHasEuro = computed(() => price.value?.startsWith("€"))
const priceFormatted = computed(() => {
  if (priceHasEuro.value) {
    return price.value?.replace("€", "").trim()
  }
  return price.value
})

const giftDescription = computed(() => gift.value?.description || null)

const strippedDescription = computed(() => {
  return giftDescription.value
    ?.replace(/(<([^>]+)>)/gi, "")
    .replace(/\s+/g, " ")
    .trim()
})

const { width: screenWidth } = useWindowSize()

const excerptLength = computed(() => {
  if (screenWidth.value < 768) {
    return 60
  }
  return hasOptions.value ? 115 : 240
})

const hasLongContent = computed(() => {
  return strippedDescription.value?.length > excerptLength.value
})

const excerpt = computed(() => {
  if (!hasLongContent.value) {
    return strippedDescription.value
  }

  return `${strippedDescription.value.substring(0, excerptLength.value)}...`
})

const showFullDescription = ref(false)

const images = computed(() => {
  return selectedOption.value?.images?.length ? selectedOption.value.images : gift.value?.images
})
const activeImage = ref(undefined)
const setActiveImage = img => {
  activeImage.value = img
}

const isKadonationVoucher = computed(() => gift.value?.is_kadonation_voucher)
const hasOptions = computed(() => options.value?.length > 0)

const getGiftInfo = async () => {
  // if giftId is not defined do nothing
  if (!props.giftId) return

  // if giftId is opened for the same gift
  if (props.giftId === gift.value?.id && gift.value) {
    return
  }

  // clear existing data
  gift.value = undefined
  options.value = undefined
  selectedOption.value = undefined
  showFullDescription.value = false
  activeImage.value = false

  try {
    const { data } = await httpClient.get(`product-grid/${props.giftId}`)
    gift.value = data.gift
    options.value = data.options
    selectedOption.value = data.options?.[0]
    setActiveImage(options.value?.length ? options.value[0].images?.[0] : gift.value.images?.[0])
  } catch (error) {
    console.error(error)
    errorToast()
    emit("close")
  }
}

watch(
  () => props.show,
  value => {
    if (!value) return

    getGiftInfo()
  },
  { immediate: true },
)

const cancel = () => {
  emit("cancel")
}
const close = () => {
  emit("close")
}
</script>
